<template>
  <div>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="600px"
      @input="initBooking()"
    >
      <template v-slot:activator="{ on }">
        <v-btn color="success" v-on="on">
          New Booking <v-icon right>add</v-icon>
        </v-btn>
      </template>
      <v-card>
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          @submit.prevent="valid && addBooking()"
        >
          <v-app-bar flat dark color="primary">
            <v-toolbar-title>New Booking</v-toolbar-title>
          </v-app-bar>
          <v-card-text class="pt-3">
            <v-container grid-list-lg class="pt-0">
              <v-flex xs6 md4 class="pb-4">
                <v-menu
                  ref="menu"
                  v-model="datePicker"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  :loading="loadingBookings"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="booking.date"
                      label="Date"
                      prepend-icon="event"
                      readonly
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="booking.date"
                    color="primary"
                    no-title
                    scrollable
                    :allowed-dates="allowedDays"
                    first-day-of-week="1"
                    :rules="[v => !!v || 'Date is required']"
                    required
                    @input="datePicker = false"
                  />
                </v-menu>
              </v-flex>
              <div class="booking-time ml-2">
                <div class="booking-time__bookings">
                  <span
                    v-for="range in bookingsRanges"
                    :key="range.position"
                    :style="`width: ${range[0]}%; left: ${range[1]}%;`"
                  >
                  </span>
                </div>
                <v-range-slider
                  v-model="booking.time"
                  color="primary"
                  :min="9"
                  :max="17"
                  thumb-color="info"
                  thumb-label="always"
                  thumb-size="40"
                  :rules="[
                    v => !!v || 'Time is required',
                    isBookingAvailable || 'Sorry this time is unavailable'
                  ]"
                  required
                >
                  <template slot="thumb-label" slot-scope="props">
                    <span class="font-weight-medium">{{ props.value }}:00</span>
                  </template>
                </v-range-slider>
              </div>
              <v-switch
                v-model="bookingFor"
                label="Book on behalf of another user?"
                color="primary"
              ></v-switch>
              <v-autocomplete
                v-if="bookingFor"
                v-model="booking.booking_for"
                :items="users"
                :loading="loadingUsers"
                hide-selected
                item-text="email"
                return-object
                label="User"
                dense
                placeholder="Start typing to find user"
                prepend-icon="person"
                :rules="[v => !!v || 'Select a user']"
                required
              ></v-autocomplete>
            </v-container>
          </v-card-text>
          <v-card-actions class="pt-0">
            <v-spacer />
            <v-btn color="error" text @click="cancel()"> Cancel </v-btn>
            <v-btn
              color="success"
              :disabled="!valid"
              type="submit"
              :loading="loadingSubmit"
            >
              Add
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { bookingsCollection, usersCollection } from '@/firebaseConfig';
import moment from 'moment';

// If it's the weekend, select the next Monday
function defaultDay() {
  let today = moment();
  if (today.isoWeekday() > 5) {
    today = today.add(1, 'weeks').isoWeekday(1);
  }
  return today.toISOString().substr(0, 10);
}

const blankBooking = {
  date: defaultDay(),
  booking_for: null,
  time: [9, 10]
};
export default {
  name: 'NewBooking',
  props: {
    roomid: {
      type: String,
      required: true
    },
    roomName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      bookings: [],
      booking: {},
      bookingFor: false,
      datePicker: false,
      dialog: false,
      loadingUsers: false,
      valid: true,
      loadingSubmit: false,
      loadingBookings: false
    };
  },
  computed: {
    userInfo() {
      let user = {
        for: this.booking.booking_for,
        current: this.$store.state.currentUserInfo
      };
      let bookFor = {};
      let current = {};

      if (user.for) {
        if (user.for.email) bookFor.email = user.for.email;
        if (user.for.id) bookFor.id = user.for.id;
        if (user.for.name) bookFor.name = user.for.name;
        if (user.for.first_name) bookFor.first_name = user.for.first_name;
        if (user.for.last_name) bookFor.last_name = user.for.last_name;
        if (user.for.business) bookFor.business = user.for.business;
      }

      if (user.current.email) current.email = user.current.email;
      if (user.current.id) current.id = user.current.id;
      if (user.current.name) current.name = user.current.name;
      if (user.current.first_name) current.first_name = user.current.first_name;
      if (user.current.last_name) current.last_name = user.current.last_name;
      if (user.current.business) current.business = user.current.business;

      return { current, bookFor };
    },
    dayStartTimestamp() {
      let date = moment(this.booking.date)
        .hour(9)
        .minutes(0)
        .format('X');
      return Number(date);
    },
    dayEndTimestamp() {
      let date = moment(this.booking.date)
        .hour(17)
        .minutes(0)
        .format('X');
      return Number(date);
    },
    bookingStartTimestamp() {
      let date = this.booking.date;
      if (date) {
        let time = `${this.booking.time[0]}:00`;
        let timestamp = new Date(`${date} ${time}`);
        return Number(moment(timestamp).format('X'));
      } else {
        return null;
      }
    },
    bookingEndTimestamp() {
      let date = this.booking.date;
      if (date) {
        let time = `${this.booking.time[1]}:00`;
        let timestamp = new Date(`${date} ${time}`);
        return Number(moment(timestamp).format('X'));
      } else {
        return null;
      }
    },
    currentLocationId() {
      return this.$store.state.currentLocation.id;
    },
    selectedDate() {
      return this.booking.date;
    },
    bookingsRanges() {
      let rangesArr = [];
      let dayLength = this.dayEndTimestamp - this.dayStartTimestamp;
      this.bookings.forEach(booking => {
        let bookingLength = booking.end_timestamp - booking.start_timestamp;
        let bookingStartToEndOfDay =
          booking.start_timestamp - this.dayStartTimestamp;
        let width = (bookingLength / dayLength) * 100;
        let position = (bookingStartToEndOfDay / dayLength) * 100;
        rangesArr.push([width, position]);
      });
      return rangesArr;
    },
    isBookingAvailable() {
      let available = true;
      let newBookingStart = this.bookingStartTimestamp;
      let newBookingEnd = this.bookingEndTimestamp;
      this.bookings.forEach(booking => {
        let bookingStart = booking.start_timestamp;
        let bookingEnd = booking.end_timestamp;
        if (
          (newBookingEnd > bookingStart && newBookingEnd <= bookingEnd) ||
          (newBookingStart >= bookingStart && newBookingStart < bookingEnd) ||
          (newBookingStart <= bookingStart && newBookingEnd >= bookingEnd)
        ) {
          available = false;
        }
      });
      return available;
    }
  },
  watch: {
    selectedDate() {
      this.getBookingsForDate();
    }
  },
  methods: {
    allowedDays(val) {
      let date = new Date(val);
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        return val;
      }
    },
    initBooking() {
      this.booking = blankBooking;
      this.getBookingsForDate();
    },
    cancel() {
      this.dialog = false;
      this.booking = blankBooking;
    },
    addBooking() {
      this.loadingSubmit = true;
      let booking = {
        booked_by: this.userInfo.current,
        booked_for: this.bookingFor
          ? this.userInfo.bookFor
          : this.userInfo.current,
        end_timestamp: this.bookingEndTimestamp,
        start_timestamp: this.bookingStartTimestamp,
        paid_date: moment().format('DD/MM/YYYY'),
        payment_status: 'paid',
        location_id: this.currentLocationId,
        room_id: this.roomid,
        room_name: this.roomName
      };
      if (this.$refs.form.validate()) {
        bookingsCollection(this.currentLocationId, this.roomid)
          .add(booking)
          .then(() => {
            this.dialog = false;
            this.$store.dispatch('createSnackbar', {
              color: 'success',
              text: `New booking created successfully`
            });
            this.booking = blankBooking;
          })
          .catch(error => {
            this.$store.dispatch('createSnackbar', {
              color: 'error',
              text: `Error adding booking: ${error}`
            });
          })
          .finally(() => {
            this.loadingSubmit = false;
          });
      }
    },
    getBookingsForDate() {
      this.loadingBookings = true;
      this.$bind(
        'bookings',
        bookingsCollection(this.currentLocationId, this.$route.params.roomid)
          .where('start_timestamp', '>=', this.dayStartTimestamp)
          .where('start_timestamp', '<=', this.dayEndTimestamp)
          .orderBy('start_timestamp')
      )
        .catch(error => {
          this.$store.dispatch('createSnackbar', {
            color: 'error',
            text: `Error loading bookings: ${error}`
          });
        })
        .finally(() => {
          this.loadingBookings = false;
        });
    }
  },
  firestore: {
    users: usersCollection
  }
};
</script>

<style lang="scss">
.booking-time {
  position: relative;
  padding-top: 6px;
  &__bookings {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    height: 34px;
    span {
      display: block;
      height: 100%;
      background-color: rgba(#fb366c, 0.4);
      position: absolute;
      top: 0;
    }
  }
  .v-input {
    position: relative;
  }
}
</style>
